import React from 'react';
import { getSchoolColor, getCollegeName, formatPercentage, formatCurrency, formatArrayField } from './designSystem';
import AIUpdateIcon from './AIUpdateIcon';

/**
 * ComparisonTable - Side-by-side comparison table with color-coded columns
 * Matches mockup's comparison table design
 */
const ComparisonTable = ({ colleges = [], metrics = [], aiUpdateHandlers = null }) => {
  if (colleges.length === 0) {
    return null;
  }

  return (
    <div className="bg-white border-2 border-slate-900 rounded-lg overflow-hidden mb-8">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr>
              <th className="bg-stone-100 text-left p-4 font-bold text-slate-900 border-b-2 border-slate-900 sticky left-0 z-20">
                Metric
              </th>
              {colleges.map((college, idx) => {
                const colors = getSchoolColor(idx);
                const name = getCollegeName(college);
                const handler = aiUpdateHandlers?.[college.id];

                return (
                  <th
                    key={college.id || idx}
                    className={`${colors.bgLight} text-left p-4 font-bold text-slate-900 border-b-2 ${colors.border} border-l-4`}
                  >
                    <div className="flex items-center gap-2">
                      <span>{name}</span>
                      {handler && (
                        <AIUpdateIcon
                          tooltip={handler.tooltip}
                          onClick={handler.onClick}
                          isLoading={handler.isLoading}
                        />
                      )}
                    </div>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {metrics.map((metric, metricIdx) => {
              const isHighlightRow = metric.highlight;

              return (
                <tr
                  key={metricIdx}
                  className={`${
                    isHighlightRow ? 'bg-stone-100' : 'border-b border-stone-200'
                  }`}
                >
                  {/* Metric Label */}
                  <td
                    className={`p-4 ${
                      isHighlightRow ? 'font-bold text-sm bg-stone-200' : 'font-medium text-sm'
                    } text-slate-900 bg-stone-50 sticky left-0 z-10`}
                  >
                    {metric.label}
                  </td>

                  {/* Values for each college */}
                  {colleges.map((college, idx) => {
                    const colors = getSchoolColor(idx);
                    let value = '';

                    // Get value based on metric field
                    if (metric.getValue) {
                      value = metric.getValue(college, idx);
                    } else if (metric.field) {
                      // Navigate nested fields (e.g., 'global_colleges.acceptance_rate')
                      const fieldParts = metric.field.split('.');
                      let val = college;
                      for (const part of fieldParts) {
                        val = val?.[part];
                      }
                      value = val;
                    }

                    // Format value based on type
                    if (metric.format === 'percentage') {
                      value = formatPercentage(value);
                    } else if (metric.format === 'currency') {
                      value = formatCurrency(value);
                    } else if (metric.format === 'array') {
                      value = formatArrayField(value, metric.maxItems || 4);
                    } else if (value === null || value === undefined) {
                      value = 'N/A';
                    }

                    return (
                      <td
                        key={`${metricIdx}-${idx}`}
                        className={`p-4 ${
                          isHighlightRow ? 'font-bold' : 'text-xs'
                        } text-slate-900 bg-white border-l-4 ${colors.border}`}
                      >
                        {value}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ComparisonTable;
